import voiceProductCreateReducer from "./VoiceProductCreateDb";
import { configureStore } from "@reduxjs/toolkit";
import { setStore } from "./storeAccess";
import generalReducer from "./GeneralDb";
import productListReducer from "./ProductListDb";
import myListingsReducer from "./MyListingsDb";
import newProductReducer from "./NewProductDb";
import messagesReducer from "./MessagesDb";
import profileReducer from "./ProfileDb";
import productDetailsReducer from "./ProductDetailsDb";
import createProductReducer from "./CreateProductDb";
import homeDbReducer from "./HomeDb";
import businessReducer from "./BusinessDb";
import cartReducer from "./CartDb";
import searchReducer from "./SearchDb";
import businessCollectionReducer from "./BusinessCollectionDb";
import businessSectionsReducer from "./BusinessSectionsDb";
import businessConfigReducer from "./BusinessConfigDb";
import ordersReducer from "./OrdersDb";
import notificationsReducer from "./NotificationsDb";
import onboardingReducer from "./OnboardingDb";
import nineSetupReducer from "./NineSetupDb";
import subscriptionsReducer from "./SubscriptionsDb";
import supportChatReducer from "./SupportChatDb";
import dineInReducer from "./DineInDb";
import dineInBoardReducer from "./DineInBoardDb";
import representativeImagesReducer from "./RepresentativeImageDb";
import imageAnalyzeReducer, {
  rehydrateAnalyzeSessions,
} from "./ImageAnalyzeDb";
import {
  loadPersistedAnalyzeSessions,
  startAnalyzeSessionSync,
} from "./ImageAnalyzePersistence";
import listingCollectionReducer from "./ListingCollectionDb";
import adminReducer from "./AdminDb";
import slotCalendarReducer from "./SlotCalendarDb";
import aiBatchReducer from "./AiBatchDb";
import catalogImportReducer from "./CatalogImportDb";
import bulkImageAnalysisV2Reducer from "./BulkImageAnalysisV2Db";

// Re-exported for anything that still imports it from here -- but the
// action itself, and every slice's own import of it, now lives in
// ./resetAllState.ts, specifically to not be part of this cycle. See
// that file's doc comment.
export { resetAllState } from "./resetAllState";

// Registered with storeAccess.ts immediately below so GenericUtility (and
// anything else outside React that needs the store) can reach it without
// importing this file -- see storeAccess.ts's doc comment.
const store = configureStore({
  reducer: {
    general: generalReducer,
    productList: productListReducer,
    myListings: myListingsReducer,
    newProduct: newProductReducer,
    messages: messagesReducer,
    profile: profileReducer,
    productDetails: productDetailsReducer,
    createProduct: createProductReducer,
    voiceProductCreate: voiceProductCreateReducer,
    homeState: homeDbReducer,
    business: businessReducer,
    cart: cartReducer,
    search: searchReducer,
    businessCollection: businessCollectionReducer,
    businessSections: businessSectionsReducer,
    businessConfig: businessConfigReducer,
    orders: ordersReducer,
    notifications: notificationsReducer,
    onboarding: onboardingReducer,
    nineSetup: nineSetupReducer,
    subscriptions: subscriptionsReducer,
    supportChat: supportChatReducer,
    dineIn: dineInReducer,
    dineInBoard: dineInBoardReducer,
    representativeImages: representativeImagesReducer,
    imageAnalyze: imageAnalyzeReducer,
    listingCollection: listingCollectionReducer,
    // Admin section state. ONE key for every admin tab (approvals,
    // subscriptions, ...) - see AdminDb for the sub-object arrangement.
    admin: adminReducer,
    // Slot Availability Calendar widget cache (ProductDetailsV2, Rental
    // listings) - see SlotCalendarDb for the byProductId cache shape.
    slotCalendar: slotCalendarReducer,
    // AI batch image edits - curator Tasks tab + admin AI Batch Edits section.
    aiBatch: aiBatchReducer,
    // Catalogue PDF import - curator Tasks tab list, processing view and the
    // review workspace (see CatalogImportDb for the cache shape).
    catalogImport: catalogImportReducer,
    // Bulk image analysis v2 ("Smart photos" tab of the Bulk upload modal) - a cache of
    // the server session, its photos, plan versions and payload (see BulkImageAnalysisV2Db).
    bulkImageAnalysisV2: bulkImageAnalysisV2Reducer,
  },
});

setStore(store);

/**
 * Photo-analysis sessions are restored from sessionStorage and then kept in
 * sync with it. Analysed drafts cost a vision-model call and a permanent
 * storage write each, so a refresh must not throw them away — see
 * ImageAnalyzePersistence for what survives and what honestly can't.
 *
 * Done here, after the store exists, rather than through `preloadedState`:
 * restoring is not a straight merge. Photos that were never analysed have no
 * bytes left on this page and have to come back marked as such, and batch
 * statuses have to be recomputed so a run interrupted by the reload doesn't
 * return as "running" forever. That logic belongs next to the rest of the
 * slice's state rules, not duplicated in a loader.
 *
 * Both calls are no-ops when there is nothing stored or storage is unavailable
 * (private browsing throws on the property itself), so neither can stop the app
 * from starting.
 */
store.dispatch(rehydrateAnalyzeSessions(loadPersistedAnalyzeSessions()));
startAnalyzeSessionSync(store);

export default store;

// Export types for use in components
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
